import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { LocationPickerMap } from '../components/LeafletMap';
import { 
  ChevronLeft, Calendar as CalendarIcon, MapPin, Clock, 
  Users, Euro, Loader2, CheckCircle, Repeat, Sparkles, Search
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr, nlBE, enGB } from 'date-fns/locale';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categories = [
  { id: 'yoga', icon: '🧘' },
  { id: 'renforcement', icon: '💪' },
  { id: 'mobilite', icon: '🤸' },
  { id: 'outdoor', icon: '🏃' },
  { id: 'running', icon: '🏃' },
  { id: 'cardio', icon: '❤️' },
  { id: 'pilates', icon: '🎯' },
  { id: 'boxe', icon: '🥊' },
  { id: 'danse', icon: '💃' },
  { id: 'padel', icon: '🏸' },
  { id: 'tennis', icon: '🎾' },
];

export default function CreateSessionPage() {
  const navigate = useNavigate();
  const { t, lang } = useTranslation();
  const dateLocale = lang === 'nl' ? nlBE : lang === 'en' ? enGB : fr;
  
  const levels = [
    { id: 'debutant', name: t('levels.beginner') },
    { id: 'intermediaire', name: t('levels.intermediate') },
    { id: 'avance', name: t('levels.advanced') },
    { id: 'tous', name: t('levels.all') },
  ];

  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(null);
  const [searchAddress, setSearchAddress] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [mapPosition, setMapPosition] = useState({ lat: 50.8503, lng: 4.3517 });
  const [activityImages, setActivityImages] = useState({});
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    level: 'tous',
    location: '',
    latitude: 50.8503,
    longitude: 4.3517,
    start_time: '09:00',
    duration_minutes: 60,
    max_participants: 10,
    min_participants: 3,
    price: 15,
    equipment_required: '',
    indoor: false,
    image_url: '',
    is_event: false,
    recurrence_type: 'none',
    recurrence_count: 1,
  });

  // Load activity image library
  React.useEffect(() => {
    fetch(`${API_URL}/api/activity-images`)
      .then(r => r.json())
      .then(data => setActivityImages(data))
      .catch(() => {});
  }, []);

  // Auto-set image when category or indoor changes
  React.useEffect(() => {
    if (formData.category && activityImages.outdoor) {
      const lib = formData.indoor && activityImages.indoor?.[formData.category]
        ? activityImages.indoor
        : activityImages.outdoor;
      if (lib[formData.category]) {
        setFormData(prev => ({ ...prev, image_url: lib[formData.category] }));
      }
    }
  }, [formData.category, formData.indoor, activityImages]);

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`
      );
      const data = await response.json();
      if (data.display_name) {
        setFormData(prev => ({ ...prev, location: data.display_name, latitude: lat, longitude: lng }));
        setSearchAddress(data.display_name);
      }
    } catch (error) {
      setFormData(prev => ({ ...prev, latitude: lat, longitude: lng }));
    }
  };

  const searchAddressHandler = async (query) => {
    if (query.length < 3) { setSearchResults([]); setShowSearchResults(false); return; }
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=be,fr`
      );
      const data = await response.json();
      setSearchResults(data);
      setShowSearchResults(true);
    } catch (error) { console.error('Search error:', error); }
  };

  const selectAddress = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData(prev => ({ ...prev, location: result.display_name, latitude: lat, longitude: lng }));
    setMapPosition({ lat, lng });
    setSearchAddress(result.display_name);
    setShowSearchResults(false);
  };

  const handleMapPositionChange = (pos) => {
    setMapPosition(pos);
    setFormData(prev => ({ ...prev, latitude: pos.lat, longitude: pos.lng }));
    reverseGeocode(pos.lat, pos.lng);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error(t('createSession.toastDateRequired')); return; }
    if (!formData.category) { toast.error(t('createSession.toastCategoryRequired')); return; }
    if (!formData.location) { toast.error(t('createSession.toastLocationRequired')); return; }

    setLoading(true);
    try {
      const sessionData = {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        duration_minutes: parseInt(formData.duration_minutes),
        max_participants: parseInt(formData.max_participants),
        min_participants: parseInt(formData.min_participants),
        price: parseFloat(formData.price),
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude),
        recurrence_count: parseInt(formData.recurrence_count),
      };

      const response = await fetch(`${API_URL}/api/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(sessionData),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.sessions) {
          toast.success(t('createSession.toastCreatedMultiple').replace('{{count}}', result.sessions.length));
        } else {
          toast.success(t('createSession.toastCreated'));
        }
        navigate('/coach/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || t('createSession.toastError'));
      }
    } catch (error) {
      toast.error(t('createSession.toastError'));
    } finally {
      setLoading(false);
    }
  };

  const recurrenceLabel = formData.recurrence_type === 'daily' ? t('createSession.consecutiveDays') : formData.recurrence_type === 'weekly' ? t('createSession.weeks') : t('createSession.months');

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-[1001] glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white transition-colors">
                <ChevronLeft className="w-6 h-6" />
              </button>
              <span className="font-bold uppercase text-lg" data-testid="create-session-title">{t('createSession.title')}</span>
            </div>
            <Link to="/">
              <img src="/logo.png" alt="Motivya" className="h-10 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-card rounded-2xl p-6">
            <h2 className="font-bold text-lg uppercase mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[var(--brand-yellow)]" />
              {t('createSession.sessionType')}
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <button type="button" onClick={() => handleChange('is_event', false)}
                className={`p-5 rounded-xl border-2 text-left transition-all ${!formData.is_event ? 'border-[var(--brand-cyan)] bg-[var(--brand-cyan)]/10' : 'border-white/10 hover:border-white/20'}`} data-testid="type-regular">
                <Repeat className={`w-8 h-8 mb-3 ${!formData.is_event ? 'text-[var(--brand-cyan)]' : 'text-white/40'}`} />
                <h3 className="font-bold mb-1">{t('createSession.regular')}</h3>
                <p className="text-sm text-white/50">{t('createSession.regularDesc')}</p>
              </button>
              <button type="button" onClick={() => { handleChange('is_event', true); handleChange('recurrence_type', 'none'); handleChange('recurrence_count', 1); }}
                className={`p-5 rounded-xl border-2 text-left transition-all ${formData.is_event ? 'border-[var(--brand-yellow)] bg-[var(--brand-yellow)]/10' : 'border-white/10 hover:border-white/20'}`} data-testid="type-event">
                <CalendarIcon className={`w-8 h-8 mb-3 ${formData.is_event ? 'text-[var(--brand-yellow)]' : 'text-white/40'}`} />
                <h3 className="font-bold mb-1">{t('createSession.uniqueEvent')}</h3>
                <p className="text-sm text-white/50">{t('createSession.uniqueEventDesc')}</p>
              </button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-card rounded-2xl p-6 space-y-5">
            <h2 className="font-bold text-lg uppercase mb-4">{t('createSession.generalInfo')}</h2>
            <div className="space-y-2">
              <Label htmlFor="title">{t('createSession.sessionTitle')}</Label>
              <Input id="title" placeholder={t('createSession.titlePlaceholder')} value={formData.title} onChange={(e) => handleChange('title', e.target.value)} className="input-modern h-14" required data-testid="session-title" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('createSession.description')}</Label>
              <Textarea id="description" placeholder={t('createSession.descriptionPlaceholder')} value={formData.description} onChange={(e) => handleChange('description', e.target.value)} rows={4} className="bg-black/20 border-white/10 rounded-xl" required data-testid="session-description" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('createSession.category')}</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange('category', v)}>
                  <SelectTrigger data-testid="session-category" className="h-14 bg-black/20 border-white/10"><SelectValue placeholder={t('createSession.select')} /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}><span className="flex items-center gap-2"><span>{cat.icon}</span><span>{t(`categories.${cat.id}`)}</span></span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('createSession.level')}</Label>
                <Select value={formData.level} onValueChange={(v) => handleChange('level', v)}>
                  <SelectTrigger data-testid="session-level" className="h-14 bg-black/20 border-white/10"><SelectValue placeholder={t('createSession.select')} /></SelectTrigger>
                  <SelectContent>
                    {levels.map((lvl) => (<SelectItem key={lvl.id} value={lvl.id}>{lvl.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Image preview - auto-selected based on category */}
            {formData.category && activityImages.outdoor && activityImages.outdoor[formData.category] && (
              <div className="space-y-2">
                <Label>{t('createSession.activityImage') || 'Image de l\'activité'}</Label>
                <div className="relative rounded-xl overflow-hidden h-40 border border-white/10">
                  <img 
                    src={formData.image_url || activityImages.outdoor[formData.category]} 
                    alt={t(`categories.${formData.category}`)}
                    className="w-full h-full object-cover"
                    data-testid="session-image-preview"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
                    <p className="text-white/70 text-xs">{formData.indoor ? (t('createSession.imageIndoor') || 'Image indoor sélectionnée') : (t('createSession.imageAutoSelected') || 'Image sélectionnée automatiquement selon la catégorie')}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="glass-card rounded-2xl p-6 space-y-5">
            <h2 className="font-bold text-lg uppercase mb-4 flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-[var(--brand-cyan)]" />
              {t('createSession.dateTime')}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>{t('createSession.startDate')}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-14 justify-start text-left font-normal bg-black/20 border-white/10 hover:bg-black/30" data-testid="session-date">
                      <CalendarIcon className="mr-2 h-4 w-4 text-white/40" />
                      {date ? format(date, 'PPP', { locale: dateLocale }) : t('createSession.select')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={date} onSelect={setDate} disabled={(date) => date < new Date()} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label htmlFor="start_time">{t('createSession.startTime')}</Label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input id="start_time" type="time" value={formData.start_time} onChange={(e) => handleChange('start_time', e.target.value)} className="input-modern pl-12 h-14" required data-testid="session-time" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('createSession.duration')}</Label>
                <Select value={String(formData.duration_minutes)} onValueChange={(v) => handleChange('duration_minutes', v)}>
                  <SelectTrigger data-testid="session-duration" className="h-14 bg-black/20 border-white/10"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">{t('createSession.dur30')}</SelectItem>
                    <SelectItem value="45">{t('createSession.dur45')}</SelectItem>
                    <SelectItem value="60">{t('createSession.dur60')}</SelectItem>
                    <SelectItem value="90">{t('createSession.dur90')}</SelectItem>
                    <SelectItem value="120">{t('createSession.dur120')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {!formData.is_event && (
              <div className="pt-4 border-t border-white/10 space-y-4">
                <Label className="flex items-center gap-2"><Repeat className="w-4 h-4 text-[var(--brand-cyan)]" />{t('createSession.recurrence')}</Label>
                <RadioGroup value={formData.recurrence_type} onValueChange={(v) => handleChange('recurrence_type', v)} className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 'none', label: t('createSession.unique'), desc: t('createSession.once') },
                    { value: 'daily', label: t('createSession.daily'), desc: t('createSession.everyDay') },
                    { value: 'weekly', label: t('createSession.weekly'), desc: t('createSession.everyWeek') },
                    { value: 'monthly', label: t('createSession.monthly'), desc: t('createSession.everyMonth') },
                  ].map(opt => (
                    <div key={opt.value}>
                      <RadioGroupItem value={opt.value} id={opt.value} className="peer sr-only" />
                      <Label htmlFor={opt.value} className="flex flex-col items-center justify-center p-4 rounded-xl border-2 border-white/10 cursor-pointer hover:border-white/20 peer-data-[state=checked]:border-[var(--brand-cyan)] peer-data-[state=checked]:bg-[var(--brand-cyan)]/10">
                        <span className="font-semibold">{opt.label}</span>
                        <span className="text-xs text-white/40">{opt.desc}</span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>

                {formData.recurrence_type !== 'none' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="space-y-2">
                    <Label htmlFor="recurrence_count">{t('createSession.occurrences')}</Label>
                    <Input id="recurrence_count" type="number" min={2} max={52} value={formData.recurrence_count} onChange={(e) => handleChange('recurrence_count', e.target.value)} className="input-modern h-14 max-w-[200px]" data-testid="recurrence-count" />
                    <p className="text-sm text-white/40">{formData.recurrence_count} {t('createSession.sessionsWillBeCreated')} ({recurrenceLabel})</p>
                  </motion.div>
                )}
              </div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="glass-card rounded-2xl p-6 space-y-5">
            <h2 className="font-bold text-lg uppercase mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[var(--brand-green)]" />
              {t('createSession.location')}
            </h2>
            <div className="space-y-2 relative">
              <Label htmlFor="location">{t('createSession.searchAddress')}</Label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input id="location" placeholder={t('createSession.addressPlaceholder')} value={searchAddress}
                  onChange={(e) => { setSearchAddress(e.target.value); searchAddressHandler(e.target.value); }}
                  onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
                  className="input-modern pl-12 h-14" data-testid="session-location" />
              </div>
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-[1002] w-full mt-1 glass-card rounded-xl overflow-hidden max-h-60 overflow-y-auto">
                  {searchResults.map((result, index) => (
                    <button key={index} type="button" onClick={() => selectAddress(result)} className="w-full px-4 py-3 text-left hover:bg-white/10 transition-colors flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-[var(--brand-green)] mt-1 flex-shrink-0" />
                      <span className="text-sm line-clamp-2">{result.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {formData.location && (
              <p className="text-sm text-[var(--brand-green)] flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                {t('createSession.locationSelected')}
              </p>
            )}
            <LocationPickerMap position={mapPosition} onPositionChange={handleMapPositionChange} className="h-[300px]" />
            <p className="text-xs text-white/40">{t('createSession.mapHint')}</p>
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <div>
                <Label htmlFor="indoor">{t('createSession.indoor')}</Label>
                <p className="text-sm text-white/40">{t('createSession.indoorDesc')}</p>
              </div>
              <Switch id="indoor" checked={formData.indoor} onCheckedChange={(v) => handleChange('indoor', v)} data-testid="session-indoor" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="glass-card rounded-2xl p-6 space-y-5">
            <h2 className="font-bold text-lg uppercase mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--brand-yellow)]" />
              {t('createSession.participantsAndPricing')}
            </h2>
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="min_participants">{t('createSession.minimum')}</Label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input id="min_participants" type="number" min={1} value={formData.min_participants} onChange={(e) => handleChange('min_participants', e.target.value)} className="input-modern pl-12 h-14" required data-testid="session-min-participants" />
                </div>
                <p className="text-xs text-white/40">{t('createSession.confirmationThreshold')}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="max_participants">{t('createSession.maximum')}</Label>
                <div className="relative">
                  <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input id="max_participants" type="number" min={1} value={formData.max_participants} onChange={(e) => handleChange('max_participants', e.target.value)} className="input-modern pl-12 h-14" required data-testid="session-max-participants" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price">{t('createSession.price')}</Label>
                <div className="relative">
                  <Euro className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input id="price" type="number" min={0} step={0.5} value={formData.price} onChange={(e) => handleChange('price', e.target.value)} className="input-modern pl-12 h-14" required data-testid="session-price" />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="equipment">{t('createSession.equipment')}</Label>
              <Input id="equipment" placeholder={t('createSession.equipmentPlaceholder')} value={formData.equipment_required} onChange={(e) => handleChange('equipment_required', e.target.value)} className="input-modern h-14" data-testid="session-equipment" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="flex gap-4">
            <Button type="button" variant="outline" className="flex-1 h-14 border-white/20" onClick={() => navigate(-1)}>{t('createSession.cancel')}</Button>
            <Button type="submit" className="btn-secondary flex-1 h-14" disabled={loading} data-testid="submit-session">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  <CheckCircle className="w-5 h-5 mr-2" />
                  {formData.recurrence_type !== 'none' && formData.recurrence_count > 1
                    ? t('createSession.createMultiple').replace('{{count}}', formData.recurrence_count)
                    : t('createSession.createSessionBtn')
                  }
                </>
              )}
            </Button>
          </motion.div>
        </form>
      </div>
    </div>
  );
}
